/**
 * @license
 * Copyright (c) 2017 The Polymer Project Authors. All rights reserved.
 * This code may only be used under the BSD style license found at
 * http://polymer.github.io/LICENSE.txt
 * The complete set of authors may be found at
 * http://polymer.github.io/AUTHORS.txt
 * The complete set of contributors may be found at
 * http://polymer.github.io/CONTRIBUTORS.txt
 * Code distributed by Google as part of the polymer project is also
 * subject to an additional IP rights grant found at
 * http://polymer.github.io/PATENTS.txt
 */

import * as estree from 'estree';

import {Annotation as JsDocAnnotation} from '../javascript/jsdoc';
import {Document, Feature, Resolvable, Severity, SourceRange, Warning} from '../model/model';

/**
 * The metadata for a JavaScript namespace.
 */
export class ScannedNamespace implements Resolvable {
  name: string;
  description?: string;
  summary?: string;
  jsdoc?: JsDocAnnotation;
  sourceRange: SourceRange;
  astNode: estree.Node;
  warnings: Warning[];

  constructor(
      name: string, description: string, summary: string, astNode: estree.Node,
      jsdoc: JsDocAnnotation, sourceRange: SourceRange) {
    this.name = name;
    this.description = description;
    this.summary = summary;
    this.jsdoc = jsdoc;
    this.sourceRange = sourceRange;
    this.astNode = astNode;
    this.warnings = [];
  }

  resolve(document: Document) {
    const foundNamespaces = document.getById(
        'namespace', this.name, {imported: true, externalPackages: true});
    if (foundNamespaces.size > 0) {
      document.warnings.push({
        message: `Found more than one namespace named ${this.name}.`,
        severity: Severity.WARNING,
        code: 'multiple-javascript-namespaces',
        sourceRange: this.sourceRange
      });
      return;
    }
    return new Namespace(this);
  }
}

export class Namespace implements Feature {
  name: string;
  description?: string;
  summary?: string;
  kinds: Set<string>;
  identifiers: Set<string>;
  sourceRange: SourceRange;
  astNode: any;
  warnings: Warning[];

  constructor(scannedNamespace: ScannedNamespace) {
    this.name = scannedNamespace.name;
    this.description = scannedNamespace.description;
    this.summary = scannedNamespace.summary;
    this.kinds = new Set(['namespace']);
    this.identifiers = new Set([this.name]);
    this.sourceRange = scannedNamespace.sourceRange;
    this.astNode = scannedNamespace.astNode;
    this.warnings = Array.from(scannedNamespace.warnings);
  }

  toString() {
    return `<Namespace id=${this.name}>`;
  }
}
